import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, UserCheck, Briefcase, IndianRupee, TrendingUp, TrendingDown,
  Clapperboard, Star, FileText, Camera, Clock, AlertCircle,
  Gift, UserPlus, ClipboardCheck, ArrowRight, CalendarClock
} from 'lucide-react';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dashboard/consolidated`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => console.error('Failed to fetch consolidated stats:', err))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <SidebarLayout title="Dashboard" subtitle="Executive Overview">
        <div className="flex items-center justify-center py-20">
          <div className="text-white text-lg">Loading...</div>
        </div>
      </SidebarLayout>
    );
  }

  const p = data?.people || {};
  const c = data?.clients || {};
  const prod = data?.production || {};
  const tal = data?.talent || {};
  const fin = data?.finance || {};
  const inv = data?.invoices || {};
  const sh = data?.shoots || {};
  const alerts = data?.alerts || {};

  return (
    <SidebarLayout title="Dashboard" subtitle="Executive Overview">
      {/* ─── Daily Brief Hero (top of dashboard) ─── */}
      <DailyBriefHero />

      {/* ─── Row 1: Primary KPIs ─── */}
      <section className="mb-8 animate-fadeIn" data-testid="dashboard-primary-kpis">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-600 mb-3">Key Metrics</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={<TrendingUp size={18} />}
            iconColor="text-emerald-400"
            label="Net Profit (FY)"
            value={fmt(fin.net_profit)}
            sub={fin.net_profit >= 0 ? 'Profitable' : 'Loss'}
            subColor={fin.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}
            testId="metric-net-profit"
          />
          <MetricCard
            icon={<IndianRupee size={18} />}
            iconColor="text-cyan-400"
            label="Total Income (FY)"
            value={fmt(fin.total_income)}
            testId="metric-total-income"
          />
          <MetricCard
            icon={<TrendingDown size={18} />}
            iconColor="text-amber-400"
            label="Total Expenses (FY)"
            value={fmt(fin.total_expenses)}
            testId="metric-total-expenses"
          />
          <MetricCard
            icon={<FileText size={18} />}
            iconColor="text-rose-400"
            label="Outstanding"
            value={fmt(fin.outstanding)}
            sub={`${inv.overdue_count || 0} overdue`}
            subColor="text-rose-400"
            testId="metric-outstanding"
          />
        </div>
      </section>

      {/* ─── Row 2: Operational KPIs ─── */}
      <section className="mb-8 animate-fadeIn" style={{ animationDelay: '0.08s' }} data-testid="dashboard-ops-kpis">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-600 mb-3">Operations</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <MiniCard icon={<Users size={16} />} iconColor="text-indigo-400" label="Employees" value={p.total_employees} testId="mini-employees" onClick={() => navigate('/employees')} />
          <MiniCard icon={<UserCheck size={16} />} iconColor="text-emerald-400" label="Active" value={p.active_employees} testId="mini-active" />
          <MiniCard icon={<Clock size={16} />} iconColor="text-cyan-400" label="Punched In Today" value={p.punched_in_today} testId="mini-punched" onClick={() => navigate('/punch-tracker')} />
          <MiniCard icon={<Briefcase size={16} />} iconColor="text-amber-400" label="Active Clients" value={`${c.active_clients}/${c.total_clients}`} testId="mini-clients" onClick={() => navigate('/clients')} />
          <MiniCard icon={<Camera size={16} />} iconColor="text-rose-400" label="Upcoming Shoots" value={sh.upcoming} testId="mini-shoots" onClick={() => navigate('/shoots')} />
          <MiniCard icon={<IndianRupee size={16} />} iconColor="text-amber-300" label="Monthly Payroll" value={fmt(p.total_payroll)} testId="mini-payroll" />
        </div>
      </section>

      {/* ─── Row 3: Production + Talent + Invoices ─── */}
      <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeIn" style={{ animationDelay: '0.16s' }} data-testid="dashboard-trackers">
        {/* Production */}
        <TrackerSummary
          title="Production"
          icon={<Clapperboard size={16} className="text-violet-400" />}
          items={[
            { label: 'In Progress', value: prod.in_progress, color: 'text-amber-400' },
            { label: 'Completed (Month)', value: prod.completed_this_month, color: 'text-emerald-400' },
          ]}
          action={() => navigate('/production')}
          testId="tracker-production"
        />
        {/* Talent */}
        <TrackerSummary
          title="Talent Pool"
          icon={<Star size={16} className="text-amber-400" />}
          items={[
            { label: 'Total Talent', value: tal.total, color: 'text-white' },
            { label: 'Influencers', value: tal.influencers, color: 'text-cyan-400' },
            { label: 'Models', value: tal.models, color: 'text-rose-400' },
          ]}
          action={() => navigate('/talent')}
          testId="tracker-talent"
        />
        {/* Invoices */}
        <TrackerSummary
          title="Invoices"
          icon={<FileText size={16} className="text-cyan-400" />}
          items={[
            { label: 'Total Invoiced', value: fmt(inv.total_invoiced), color: 'text-white' },
            { label: 'Received', value: fmt(inv.total_received), color: 'text-emerald-400' },
            { label: 'Overdue', value: inv.overdue_count, color: 'text-rose-400' },
          ]}
          action={() => navigate('/invoices')}
          testId="tracker-invoices"
        />
      </section>

      {/* ─── Row 4: Alerts & Action Items ─── */}
      <section className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-fadeIn" style={{ animationDelay: '0.24s' }} data-testid="dashboard-alerts">
        {/* Pending Actions */}
        <div className="alert-card border-l-4 border-l-indigo-500" data-testid="alert-pending-actions">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck size={16} className="text-indigo-400" />
            <h3 className="font-semibold text-white text-sm">Pending Actions</h3>
          </div>
          <div className="space-y-2.5">
            <PendingRow label="Approvals" count={alerts.pending_approvals} onClick={() => navigate('/approvals')} />
            <PendingRow label="Leave Requests" count={alerts.pending_leaves} onClick={() => navigate('/approvals')} />
            <PendingRow label="Reimbursements" count={alerts.pending_reimbursements} onClick={() => navigate('/approvals')} />
          </div>
        </div>

        {/* Birthdays & Reviews */}
        <div className="alert-card border-l-4 border-l-emerald-500" data-testid="alert-birthdays-reviews">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={16} className="text-emerald-400" />
            <h3 className="font-semibold text-white text-sm">Birthdays & Reviews</h3>
          </div>
          <div className="space-y-2">
            {(alerts.upcoming_birthdays || []).length === 0 && (alerts.upcoming_reviews || []).length === 0 ? (
              <p className="text-sm text-slate-500">Nothing this week</p>
            ) : (
              <>
                {(alerts.upcoming_birthdays || []).slice(0, 3).map(b => (
                  <div key={b.employee_id} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Gift size={12} className="text-emerald-400" />
                      <span className="text-slate-300 text-sm">{b.name}</span>
                    </div>
                    <span className="text-xs text-emerald-400">{b.days_until === 0 ? 'Today!' : `${b.days_until}d`}</span>
                  </div>
                ))}
                {(alerts.upcoming_reviews || []).slice(0, 3).map(r => (
                  <div key={r.employee_id} className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CalendarClock size={12} className="text-rose-400" />
                      <span className="text-slate-300 text-sm">{r.name}</span>
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(r.review_date)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* New Joinees */}
        <div className="alert-card border-l-4 border-l-cyan-500" data-testid="alert-new-joinees">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={16} className="text-cyan-400" />
            <h3 className="font-semibold text-white text-sm">New Joinees (This Month)</h3>
          </div>
          <div className="space-y-2">
            {(alerts.new_joinees || []).length === 0 ? (
              <p className="text-sm text-slate-500">No new joinees this month</p>
            ) : (
              (alerts.new_joinees || []).slice(0, 5).map(j => (
                <div key={j.employee_id} className="flex justify-between items-center">
                  <span className="text-slate-300 text-sm">{j.name}</span>
                  <span className="text-xs text-slate-500">{j.department}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </SidebarLayout>
  );
}

/* ─── Sub-components ─── */

function MetricCard({ icon, iconColor, label, value, sub, subColor, testId }) {
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500">{label}</span>
        <span className={iconColor}>{icon}</span>
      </div>
      <div className="font-heading text-2xl md:text-3xl font-black tracking-tight text-white">{value}</div>
      {sub && <p className={`text-xs mt-1 ${subColor || 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}

function MiniCard({ icon, iconColor, label, value, testId, onClick }) {
  return (
    <div
      className={`glass-card p-4 flex flex-col gap-2 ${onClick ? 'cursor-pointer' : ''}`}
      data-testid={testId}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <span className={iconColor}>{icon}</span>
        <span className="text-[10px] font-semibold tracking-widest uppercase text-slate-500">{label}</span>
      </div>
      <span className="font-heading text-xl font-bold text-white">{value}</span>
    </div>
  );
}

function TrackerSummary({ title, icon, items, action, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        <button onClick={action} className="text-slate-500 hover:text-white transition-colors" data-testid={`${testId}-link`}>
          <ArrowRight size={14} />
        </button>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.label} className="flex justify-between items-center">
            <span className="text-xs text-slate-400">{item.label}</span>
            <span className={`font-heading text-base font-bold ${item.color}`}>{item.value ?? 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingRow({ label, count, onClick }) {
  return (
    <div
      className={`flex justify-between items-center ${onClick ? 'cursor-pointer hover:bg-white/[0.02] -mx-2 px-2 py-1 rounded transition-colors' : ''}`}
      onClick={onClick}
    >
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`font-heading text-sm font-bold ${count > 0 ? 'text-amber-400' : 'text-slate-600'}`}>
        {count || 0}
      </span>
    </div>
  );
}


// ─── Daily Brief Hero Card ───
function DailyBriefHero() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/daily-brief/today`, { withCredentials: true })
      .then(res => setBrief(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-4 mb-6 border border-indigo-500/20 animate-pulse" data-testid="daily-brief-hero-loading">
        <div className="h-4 bg-white/5 rounded w-40 mb-2" />
        <div className="h-3 bg-white/5 rounded w-full" />
      </div>
    );
  }
  if (!brief) return null;

  const narrative = brief.narrative || '';
  // Extract just the exec summary paragraph (between "## Executive Summary" and next ##)
  const execMatch = narrative.match(/## Executive Summary\s*\n([\s\S]*?)(?=\n##|$)/);
  const execSummary = execMatch ? execMatch[1].trim() : narrative.slice(0, 350);
  const attentionCount = brief.attention_list?.length || 0;
  const highSeverity = brief.attention_list?.filter(a => a.severity === 'high').length || 0;

  return (
    <section
      className="mb-6 p-5 rounded-xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-transparent cursor-pointer hover:border-indigo-500/40 transition-colors group"
      onClick={() => navigate('/daily-brief')}
      data-testid="daily-brief-hero"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
          <ClipboardCheck size={18} className="text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-indigo-300">Daily Brief</span>
            <span className="text-[10px] text-slate-500">· {brief.date}</span>
            {attentionCount > 0 && (
              <span className={`text-[10px] px-2 py-0.5 rounded border ${highSeverity > 0 ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
              </span>
            )}
          </div>
          <p className="text-sm text-slate-300 leading-relaxed line-clamp-3" data-testid="daily-brief-summary">
            {execSummary || 'Morning brief is being prepared...'}
          </p>
          <div className="flex items-center gap-1 mt-3 text-xs text-indigo-400 group-hover:text-indigo-300 transition-colors">
            <span className="font-semibold">Open full brief</span>
            <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
          </div>
        </div>
      </div>
    </section>
  );
}
